import { RiseOutlined, SoundOutlined, TagOutlined } from '@ant-design/icons';
import { Button, Modal, Steps } from 'antd';
import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from '../../contexts/AppContext';
import { ReactComponent as ShopBackWordmark } from '../moop-shell/shopback-wordmark.svg';
import { GETTING_STARTED_ITEMS, GettingStartedItem } from './gettingStarted';
import { useOnboardingTour } from './OnboardingTourContext';
import styles from './WelcomeModal.module.scss';

const { Step } = Steps;

/**
 * Welcome modal — first-run splash.
 *
 * Left panel: welcome copy + Getting-started vertical stepper. Clicking
 * any step launches that task's guided tour immediately. Two buttons at
 * the bottom offer a shortcut to the first task ("Take the tour") or an
 * escape hatch ("Later").
 *
 * Right panel: animated hero — three campaign-type cards fanning in on
 * a synchronised loop. Purely decorative, but reinforces the three
 * campaign shapes the merchant can book from the portal.
 */

interface CampaignFacet {
  key: 'adsales' | 'upsize' | 'offer';
  label: string;
  Icon: typeof SoundOutlined;
  accent: string;
  tint: string;
  hint: string;
}

// Kept in stack order: back → middle → front. The keyframes reveal them
// in this same order via per-position keyframes (heroCardBack/Mid/Front).
const CAMPAIGN_FACETS: CampaignFacet[] = [
  {
    key: 'adsales',
    label: 'Ad Sales',
    Icon: SoundOutlined,
    accent: '#d4a017',
    tint: '#fff6dc',
    hint: 'Featured across ShopBack',
  },
  {
    key: 'upsize',
    label: 'Upsize',
    Icon: RiseOutlined,
    accent: '#3b82f6',
    tint: '#e0edff',
    hint: 'Boost your cashback rate',
  },
  { key: 'offer', label: 'Offer', Icon: TagOutlined, accent: '#22a06b', tint: '#defbe6', hint: 'Shopper-facing deals' },
];

const WelcomeModal: React.FC = () => {
  const history = useHistory();
  const { welcomeVisible, dismissWelcome, startTaskTour, markVisitedHelp, completedTasks, setChainMode } =
    useOnboardingTour();
  const { user } = useContext(AppContext);

  const isAdmin = useMemo(() => {
    if (!user || typeof user !== 'object') return false;
    const u = user as { permissions?: { accessAdmin?: boolean } };
    return Boolean(u.permissions?.accessAdmin);
  }, [user]);

  const items = useMemo(() => GETTING_STARTED_ITEMS.filter((it) => !it.adminOnly || isAdmin), [isAdmin]);

  // "Take the tour" primary starts with the first unfinished task, or
  // the very first task if the merchant has completed nothing yet.
  // Also marks the Help nav as visited so the sidebar spot + tooltip
  // don't appear on top of the tour — the merchant is already being
  // guided, no need for a second nudge pointing them elsewhere.
  const handleTakeTour = (): void => {
    const firstUnfinished: GettingStartedItem | undefined = items.find((it) => !completedTasks.has(it.key)) ?? items[0];
    if (!firstUnfinished) {
      dismissWelcome();
      return;
    }
    markVisitedHelp();
    dismissWelcome();
    setChainMode(true);
    history.push(firstUnfinished.route);
    startTaskTour(firstUnfinished.key);
  };

  return (
    <Modal
      visible={welcomeVisible}
      width={880}
      centered
      closable={false}
      maskClosable={false}
      title={null}
      footer={null}
      bodyStyle={{ padding: 0 }}
      className={styles.modal}
    >
      <div className={styles.layout}>
        {/* ── Left panel: copy + task stepper (centred) + CTAs (bottom) ── */}
        <div className={styles.left}>
          <div className={styles.leftContent}>
            <span className={styles.chip}>New</span>
            <h2 className={styles.title}>Welcome to ShopBack for Business</h2>
            <p className={styles.body}>Reach millions of shoppers with ShopBack.</p>

            {/* Informational preview only. Each step is explicitly set to
                'wait' (or 'finish' if already completed) so antd never
                paints the "process" state — no blue/active first row. */}
            <Steps direction="vertical" size="small" current={-1} className={styles.stepper}>
              {items.map((item) => (
                <Step key={item.key} title={item.title} status={completedTasks.has(item.key) ? 'finish' : 'wait'} />
              ))}
            </Steps>
          </div>

          <div className={styles.actions}>
            <Button size="large" type="primary" onClick={handleTakeTour} className={styles.primaryBtn}>
              Take the tour
            </Button>
            <Button size="large" onClick={dismissWelcome} className={styles.secondaryBtn}>
              Later
            </Button>
          </div>
        </div>

        {/* ── Right panel: animated card hero ──
            Three campaign-type cards revealing one by one on a shared
            4.2s loop, then all clear together and the cycle repeats. */}
        <div className={styles.right} aria-hidden="true">
          <ShopBackWordmark className={styles.brandmark} aria-label="ShopBack" />
          <div className={styles.heroStack}>
            {CAMPAIGN_FACETS.map((facet, i) => {
              const { Icon } = facet;
              return (
                <div
                  key={facet.key}
                  className={styles.heroCard}
                  style={
                    {
                      ['--accent' as string]: facet.accent,
                      ['--tint' as string]: facet.tint,
                      ['--stack-index' as string]: String(i),
                    } as React.CSSProperties
                  }
                >
                  <div className={styles.heroCardHead}>
                    <span className={styles.heroIcon}>
                      <Icon style={{ fontSize: 18 }} />
                    </span>
                    <span className={styles.heroLabel}>{facet.label}</span>
                  </div>
                  <div className={styles.heroHint}>{facet.hint}</div>
                  <div className={styles.heroBarLg} />
                  <div className={styles.heroBarSm} />
                </div>
              );
            })}
          </div>
          {/* Ambient drift particles */}
          <span className={`${styles.particle} ${styles.p1}`} />
          <span className={`${styles.particle} ${styles.p2}`} />
          <span className={`${styles.particle} ${styles.p3}`} />
          <span className={`${styles.particle} ${styles.p4}`} />
          <span className={`${styles.particle} ${styles.p5}`} />
        </div>
      </div>
    </Modal>
  );
};

export default WelcomeModal;
